/**
 * 代码同步 Hook
 * 
 * 负责管理 AI 对话与代码编辑器之间的双向数据同步：
 * - AI 生成代码 → 自动填充到编辑器
 * - 用户点击按钮 → 手动将代码发送给 AI
 * - 检测 AI 响应中的代码块
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { plcCodeService } from '../services/plcCodeService';
import type {
  PlcLanguageType,
  ParsedCodeBlock,
  CodeSyncEvent
} from '../types';

// ============================================
// 类型定义
// ============================================

/** Hook 配置选项 */
export interface UseCodeSyncOptions {
  /** 初始代码 */
  readonly initialCode?: string;
  /** 初始语言 */
  readonly initialLanguage?: PlcLanguageType;
  /** 是否自动同步 AI 生成的代码到编辑器 */
  readonly autoSyncFromAI?: boolean;
  /** 
   * AI 代码同步策略
   * - 'auto': 直接覆盖编辑器（默认 — 编辑器无内容时）
   * - 'manual': 弹窗确认后覆盖
   * - 'diff': 进入 diff 审查模式（编辑器有内容时）
   */
  readonly syncStrategy?: 'auto' | 'manual' | 'diff';
  /** 代码变更回调 */
  readonly onCodeChange?: (code: string, language: PlcLanguageType) => void;
  /** 检测到代码块回调 */
  readonly onCodeDetected?: (block: ParsedCodeBlock) => void;
}

/** Hook 返回值 */
export interface UseCodeSyncReturn {
  /** 当前代码 */
  readonly code: string;
  /** 当前语言 */
  readonly language: PlcLanguageType;
  /** 是否有未保存修改 */
  readonly isDirty: boolean;
  /** 是否正在从 AI 接收代码 */
  readonly isReceivingFromAI: boolean;
  /** 最近检测到的代码块（用于弹窗提示） */
  readonly detectedCodeBlock: ParsedCodeBlock | null;
  /** 同步事件历史 */
  readonly syncHistory: readonly CodeSyncEvent[];
  
  // 操作方法
  /** 更新代码 */
  readonly setCode: (code: string) => void;
  /** 更新语言 */
  readonly setLanguage: (language: PlcLanguageType) => void;
  /** 处理 AI 响应文本，提取代码块 */
  readonly processAIResponse: (text: string) => ParsedCodeBlock | null;
  /** 将检测到的代码应用到编辑器 */
  readonly applyDetectedCode: () => void;
  /** 忽略检测到的代码 */
  readonly dismissDetectedCode: () => void;
  /** 生成发送给 AI 的代码上下文 */
  readonly generateAIContext: () => string;
  /** 重置状态 */
  readonly reset: () => void;
  /** 标记为已保存 */
  readonly markAsSaved: () => void;
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
const createId = (): string =>
  `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/** 获取当前 ISO 时间 */
const nowIso = (): string => new Date().toISOString();

// ============================================
// Hook 实现
// ============================================

export const useCodeSync = (
  options: UseCodeSyncOptions = {}
): UseCodeSyncReturn => {
  const {
    initialCode = '',
    initialLanguage = 'st',
    autoSyncFromAI = true,
    syncStrategy = 'auto',
    onCodeChange,
    onCodeDetected
  } = options;

  // ========================================
  // 状态
  // ========================================
  
  /** 当前代码 */
  const [code, setCodeState] = useState<string>(initialCode);
  
  /** 当前语言 */
  const [language, setLanguageState] = useState<PlcLanguageType>(initialLanguage);
  
  /** 是否有未保存修改 */
  const [isDirty, setIsDirty] = useState<boolean>(false);
  
  /** 是否正在从 AI 接收代码 */
  const [isReceivingFromAI, setIsReceivingFromAI] = useState<boolean>(false);
  
  /** 最近检测到的代码块 */
  const [detectedCodeBlock, setDetectedCodeBlock] = useState<ParsedCodeBlock | null>(null);
  
  /** 同步事件历史 */
  const [syncHistory, setSyncHistory] = useState<readonly CodeSyncEvent[]>([]);
  
  // 用于保存初始代码的引用（判断是否修改）
  const savedCodeRef = useRef<string>(initialCode);

  // ========================================
  // 添加同步事件到历史
  // ========================================
  const addSyncEvent = useCallback(
    (type: CodeSyncEvent['type'], eventCode: string, eventLanguage: PlcLanguageType): void => {
      const event: CodeSyncEvent = {
        type,
        code: eventCode,
        language: eventLanguage,
        timestamp: nowIso()
      };
      
      setSyncHistory(prev => [...prev.slice(-19), event]); // 保留最近 20 条
    },
    []
  );

  // ========================================
  // 更新代码
  // ========================================
  const setCode = useCallback(
    (newCode: string): void => {
      setCodeState(newCode);
      setIsDirty(newCode !== savedCodeRef.current);
      addSyncEvent('code-updated', newCode, language);
      onCodeChange?.(newCode, language);
    },
    [language, onCodeChange, addSyncEvent]
  );

  // ========================================
  // 更新语言
  // ========================================
  const setLanguage = useCallback(
    (newLanguage: PlcLanguageType): void => {
      setLanguageState(newLanguage);
      onCodeChange?.(code, newLanguage);
    },
    [code, onCodeChange]
  );

  // ========================================
  // 处理 AI 响应，提取代码块，支持 diff 策略
  // ========================================
  const processAIResponse = useCallback(
    (text: string): ParsedCodeBlock | null => {
      setIsReceivingFromAI(true);
      
      try {
        // 提取第一个 PLC 代码块，优先匹配当前语言
        const block = plcCodeService.extractFirstPlcCode(text, language);
        
        if (!block) {
          setIsReceivingFromAI(false);
          return null;
        }
        
        // 通知外部
        onCodeDetected?.(block);
        
        if (autoSyncFromAI) {
          // 自动同步模式：直接应用代码
          const targetLanguage = block.language !== 'unknown' 
            ? block.language 
            : language;
          
          // ✨ 修改：根据策略决定行为
          //    当编辑器已有代码且策略为 diff 时，不直接覆盖
          const currentCode = code;
          const hasExistingCode = currentCode.trim().length > 0;

          if (hasExistingCode && syncStrategy === 'diff') {
            // diff 模式：保存到 detectedCodeBlock，由外层进入 diff 审查
            setDetectedCodeBlock(block);
          } else {
            // auto 模式或编辑器为空：直接应用
            setCodeState(block.content);
            setLanguageState(targetLanguage);
            setIsDirty(true);
            addSyncEvent('code-from-ai', block.content, targetLanguage);
            onCodeChange?.(block.content, targetLanguage);
          }
        } else {
          // 手动确认模式：保存代码块，等待用户确认
          setDetectedCodeBlock(block);
        }
        
        setIsReceivingFromAI(false);
        return block;
      } catch (error) {
        console.error('[useCodeSync] 解析 AI 响应失败:', error);
        setIsReceivingFromAI(false);
        return null;
      }
    },
    [language,  code, autoSyncFromAI, syncStrategy, onCodeDetected, onCodeChange, addSyncEvent]
  );

  // ========================================
  // 应用检测到的代码
  // ========================================
  const applyDetectedCode = useCallback((): void => {
    if (!detectedCodeBlock) {
      return;
    }
    
    const targetLanguage = detectedCodeBlock.language !== 'unknown'
      ? detectedCodeBlock.language
      : language;
    
    setCodeState(detectedCodeBlock.content);
    setLanguageState(targetLanguage);
    setIsDirty(true);
    addSyncEvent('code-from-ai', detectedCodeBlock.content, targetLanguage);
    onCodeChange?.(detectedCodeBlock.content, targetLanguage);
    setDetectedCodeBlock(null);
  }, [detectedCodeBlock, language, onCodeChange, addSyncEvent]);

  // ========================================
  // 忽略检测到的代码
  // ========================================
  const dismissDetectedCode = useCallback((): void => {
    setDetectedCodeBlock(null);
  }, []);

  // ========================================
  // 生成发送给 AI 的代码上下文
  // ========================================
  const generateAIContext = useCallback((): string => {
    if (!code.trim()) {
      return '';
    }
    
    const langNames: Record<PlcLanguageType, string> = {
      st: '结构化文本 (ST)',
      il: '指令表 (IL)',
      lad: '梯形图 (LAD)',
      fbd: '功能块图 (FBD)'
    };
    
    const context = [
      `当前 PLC 代码（${langNames[language]}）：`,
      '```' + language,
      code,
      '```',
      '',
      '请基于以上代码进行分析或优化。'
    ].join('\n');
    
    addSyncEvent('code-to-chat', code, language);
    
    return context;
  }, [code, language, addSyncEvent]);

  // ========================================
  // 重置状态
  // ========================================
  const reset = useCallback((): void => {
    setCodeState(initialCode);
    setLanguageState(initialLanguage);
    setIsDirty(false);
    setIsReceivingFromAI(false);
    setDetectedCodeBlock(null);
    savedCodeRef.current = initialCode;
  }, [initialCode, initialLanguage]);

  // ========================================
  // 标记为已保存
  // ========================================
  const markAsSaved = useCallback((): void => {
    savedCodeRef.current = code;
    setIsDirty(false);
  }, [code]);

  // ========================================
  // 监听外部事件（发送代码到 AI）
  // ========================================
  useEffect(() => {
    const handleSendToAI = (event: Event): void => {
      const customEvent = event as CustomEvent<{ code: string; language: PlcLanguageType }>;
      const { code: eventCode, language: eventLanguage } = customEvent.detail;
      
      // 可以在这里触发发送到 AI 的逻辑
      // 具体实现取决于父组件如何处理
      console.log('[useCodeSync] 收到发送代码到 AI 的请求:', {
        codeLength: eventCode.length,
        language: eventLanguage
      });
    };
    
    window.addEventListener('plc-send-to-ai', handleSendToAI);
    
    return () => {
      window.removeEventListener('plc-send-to-ai', handleSendToAI);
    };
  }, []);

  // ========================================
  // 返回
  // ========================================
  return {
    // 状态
    code,
    language,
    isDirty,
    isReceivingFromAI,
    detectedCodeBlock,
    syncHistory,
    
    // 方法
    setCode,
    setLanguage,
    processAIResponse,
    applyDetectedCode,
    dismissDetectedCode,
    generateAIContext,
    reset,
    markAsSaved
  };
};

export default useCodeSync;